import { PresignOperation, PresignResponse } from "./types";
import { presignApiRequestSchema, presignApiResponseSchema } from "./schemas";
import { ALLOWED_MIME_TYPES } from "@/constants";
import { parseResponseError } from "./utils/parseResponseError";

export interface PresignRequest {
  presignUrl: string;
  fieldName: string;
  filename: string;
  operation: PresignOperation;
}

export async function requestPresignedUrl(
  params: PresignRequest,
): Promise<PresignResponse> {
  const { presignUrl, fieldName, filename, operation } = params;
  const requestBody = presignApiRequestSchema.parse({
    filename,
    field_name: fieldName,
    operation,
  });

  const response = await fetch(presignUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorMessage = await parseResponseError(response);
    throw new Error(`Failed to request presigned URL: ${errorMessage}`);
  }

  const body = await response.json();
  const parsed = presignApiResponseSchema.parse(body);
  const payload = parsed.data;

  return {
    filename: payload.filename,
    fieldName: payload.field_name,
    s3Key: payload.s3_key,
    presignedUrl: payload.presigned_url,
    operation: payload.operation,
    contentType: payload.content_type,
    metadata: payload.metadata,
  };
}

const buildUploadHeaders = (
  contentType?: string,
  fileType?: string,
  metadata?: { confirmed_at?: string | null },
): Record<string, string> => {
  const headers: Record<string, string> = {};
  const finalContentType =
    contentType || fileType || "application/octet-stream";

  if (ALLOWED_MIME_TYPES.includes(finalContentType)) {
    headers["Content-Type"] = finalContentType;
  }

  if (metadata?.confirmed_at) {
    headers["x-amz-meta-confirmed_at"] = metadata.confirmed_at;
  }

  return headers;
};

export async function uploadWithPresignedUrl(
  presignedUrl: string,
  file: File,
  contentType?: string,
  metadata?: { confirmed_at?: string | null },
): Promise<void> {
  const headers = buildUploadHeaders(contentType, file.type, metadata);

  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers,
  });

  if (!response.ok) {
    const errorMessage = await parseResponseError(response);
    throw new Error(`Failed to upload file: ${errorMessage}`);
  }
}

/**
 * Deletes a file from S3 using a presigned URL.
 */
export async function deleteWithPresignedUrl(
  presignedUrl: string,
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorMessage = await parseResponseError(response);
    throw new Error(`Failed to delete file: ${errorMessage}`);
  }
}
